import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import { useTranslation } from "i18n-fe";
import { syncEngine } from "../local/sync";
import { queueRideReview } from "../local/reviewRequests";

// #3088 - the scribe's REVIEW button: "something weird happened, or I messed
// something up". Confirms with an optional comment, then queues the ride for
// the scoreadmin review queue through the offline outbox
// (local/reviewRequests.js) and hands control back via onSent, which the
// caller uses to return the scribe to the ride list. Never waits on the
// network, so it behaves identically offline.
//
// #3378 moved this out of the footer into an orange "!" toggle under the test;
// #3633 reverses that: the "!" read as an error, so it is a plain outlined
// neutral "Send for review" button again, at the far left of the sticky
// footer (ScoringForm), well away from Confirm Score / Complete & Sign. It
// opens the confirm dialog directly, and the old explanation text now sits at
// the top of the dialog body.
export default function ReviewRideButton({ rideId, isTempRide = false, onSent, disabled = false }) {
  const { t } = useTranslation("score");
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const close = () => {
    if (sending) return;
    setOpen(false);
    setError(false);
  };

  const confirm = async () => {
    setSending(true);
    setError(false);
    try {
      await queueRideReview({ rideId, isTempRide, comment });
    } catch {
      setSending(false);
      setError(true);
      return;
    }
    // Fire-and-forget: scribe-fe's axios has no timeout, so awaiting a flush
    // here could hang the scribe on a flaky venue connection. The engine's
    // own tick drains the op; this only nudges it and refreshes the badge.
    if (syncEngine.isOnline()) void syncEngine.flush();
    else void syncEngine.refreshPending();
    setSending(false);
    setOpen(false);
    setComment("");
    onSent?.();
  };

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        startIcon={<OutlinedFlagIcon />}
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid="review-ride-button"
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        {t("scoring.reviewFooterButton")}
      </Button>
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>{t("scoring.reviewDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }} data-testid="review-explanation">
            {t("scoring.reviewExplain")}
          </Typography>
          <DialogContentText sx={{ mb: 2 }}>{t("scoring.reviewDialogBody")}</DialogContentText>
          <TextField
            autoComplete="off"
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t("scoring.reviewCommentLabel")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            slotProps={{
              htmlInput: { maxLength: 1000, "data-testid": "review-comment-input" },
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="review-error">
              {t("scoring.reviewError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={sending} onClick={close} data-testid="review-cancel-button">
            {t("scoring.reviewCancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            loading={sending}
            onClick={confirm}
            data-testid="review-confirm-button"
          >
            {t("scoring.reviewConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
